import { useEffect } from "react";
import { Link } from "react-router-dom";
import MagazineLayout from "@/components/MagazineLayout";
import { Reveal } from "@/components/Reveal";

export default function About() {
  useEffect(() => { document.title = "About — S/ME"; }, []);

  return (
    <MagazineLayout>
      <div className="pt-[68px] bg-cream min-h-screen" data-testid="about-page">
        <header className="px-5 sm:px-8 pt-16 sm:pt-24 pb-10 max-w-[1100px] mx-auto">
          <p className="overline text-gloss-silver mb-4">The Manifesto</p>
          <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-none">What is S/ME.</h1>
          <p className="font-sans text-xl sm:text-2xl text-ink/70 mt-6 max-w-2xl leading-relaxed">
            Business, made desirable. S/ME is the bold new online magazine for the UK's independent business owners —
            where the people who actually keep this country running become the cover stars.
          </p>
        </header>

        <section className="px-5 sm:px-8 pb-16 max-w-[1100px] mx-auto">
          <div className="border-t border-ink/20 pt-12 grid grid-cols-1 lg:grid-cols-3 gap-x-12 gap-y-10">
            <Reveal>
              <div>
                <p className="overline text-gloss-gold mb-3">Why we exist</p>
                <p className="font-sans text-base text-ink/80 leading-relaxed">
                  The big magazines put celebrities on the cover. We put the tailor in Marylebone, the potter in St Ives,
                  the bakery in the Northern Quarter. Real British businesses, shot and written like the icons they are —
                  because visibility is the difference between a business that survives and one that's loved.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.06}>
              <div>
                <p className="overline text-gloss-gold mb-3">How it works</p>
                <p className="font-sans text-base text-ink/80 leading-relaxed">
                  Every month is a fresh issue. Members get a cinematic cover shoot, a written feature, an advert, social
                  content and a place in our directory — the same editorial treatment, month after month. Use your own
                  photography, or let our studio craft the full glossy treatment from the photos you share — your call,
                  and either way you simply submit once and the desk takes it from there. Readers get
                  cutting-edge stories on culture and money — read on the page or heard aloud with a tap of the Listen
                  button — and when they ask our Ask S/ME concierge who to hire, it answers with our members. The
                  flywheel: great content pulls readers, readers see our
                  members, members get clients, members stay for life.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div>
                <p className="overline text-gloss-gold mb-3">Our promise</p>
                <p className="font-sans text-base text-ink/80 leading-relaxed">
                  No corporate stock photos. No filler. Everything is crafted to make small business feel cool, stylish and
                  worth paying attention to — because when the UK's independents thrive, the whole country gets more interesting.
                </p>
                <Link to="/join" className="inline-block mt-6 overline bg-ink text-cream px-6 py-3 hover:bg-gloss-gold hover:text-ink transition-colors" data-testid="about-join">
                  Become a cover star →
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        {/* one magazine, the whole country */}
        <section className="bg-ink text-cream px-5 sm:px-8 py-16 sm:py-20 grain" data-testid="about-one-magazine">
          <div className="max-w-[1100px] mx-auto relative z-10">
            <Reveal>
              <p className="overline text-gloss-gold mb-4">One magazine, the whole country</p>
              <h2 className="font-serif text-3xl sm:text-5xl tracking-tight leading-[0.95] max-w-3xl">
                Our members serve the whole UK — wherever they hang their sign.
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 mt-8 max-w-4xl">
                <p className="font-sans text-base text-cream/80 leading-relaxed">
                  S/ME isn't split into local editions, because our members' businesses aren't local in the old sense.
                  Many are <span className="text-cream italic font-serif">destination businesses</span> — the restaurant,
                  studio or shop that's genuinely worth the journey, from anywhere.
                </p>
                <p className="font-sans text-base text-cream/80 leading-relaxed">
                  And many are <span className="text-cream italic font-serif">remote and online businesses</span> that serve
                  customers wherever they happen to be. That's why every member sits together in one national magazine —
                  the area on a profile is context, never a boundary.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="px-5 sm:px-8 py-20 max-w-[1100px] mx-auto">
          <blockquote className="border-l-2 border-gloss-gold pl-6 sm:pl-10 max-w-3xl">
            <p className="font-serif text-3xl sm:text-4xl leading-snug tracking-tight">
              "This country is built by its small businesses. We just make sure they get seen like they deserve."
            </p>
            <p className="overline text-ink/50 mt-5">— The Editors, S/ME</p>
          </blockquote>
        </section>
      </div>
    </MagazineLayout>
  );
}
